/* Collection page behaviour (sections/main-collection, docs/17 §1).

   <mw-collection> is an enhancement layer, never the mechanism: the filter and sort forms are
   real GET forms to the collection URL and every page link is a real link, so the page filters,
   sorts and paginates with JS switched off. With JS this module keeps the same URLs but fetches
   them through the Section Rendering API (?section_id=…), swaps only the parts that changed,
   pushes the URL and puts focus back where it was.

   It owns five behaviours:
   - facets: change / submit on [data-facets-form] and [data-sort-form]
   - navigation: [data-facets-link] (pages), [data-facets-more] (load more), [data-facets-clear]
   - the sidebar toggle and the filter dialog trigger (the dialog itself is the shared overlay
     system, docs/15 §9 — this only opens it)
   - «Show more» inside one filter
   - the column switcher, a view preference written to <html> as --mw-cols / --mw-cols-t /
     --mw-cols-m and remembered in localStorage, which is why it survives a filter swap and a
     reload. The product list reads those variables as the fallback of its own column values.

   Price inputs and sliders are one control: the two range thumbs and the two number fields write
   to each other, never cross, and apply after a short pause so dragging does not fire a request
   per pixel. */

const COLS = [
  ['d', 'mw:cols', '--mw-cols'],
  ['t', 'mw:cols-t', '--mw-cols-t'],
  ['m', 'mw:cols-m', '--mw-cols-m'],
];

const store = {
  get(key) {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {
      /* Private mode or blocked storage: the preference just does not persist. */
    }
  },
};

/* Empty values would otherwise become `filter.v.price.gte=` and count as an active filter. */
function urlFromForm(form) {
  const params = new URLSearchParams();
  for (const [name, value] of new FormData(form)) {
    if (String(value).trim() !== '') params.append(name, value);
  }
  const action = form.getAttribute('action') || window.location.pathname;
  const query = params.toString();
  return query ? `${action}?${query}` : action;
}

class Collection extends HTMLElement {
  connectedCallback() {
    this.results = this.querySelector('[data-facets-results]');
    this.page = this.querySelector('.cpage');
    this.abort = null;
    this.priceTimer = 0;

    this.onChange = this.onChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onPop = this.onPop.bind(this);

    this.onKeydown = this.onKeydown.bind(this);
    this.onOutside = this.onOutside.bind(this);
    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onOutside);

    this.addEventListener('change', this.onChange);
    this.addEventListener('input', this.onInput);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    window.addEventListener('popstate', this.onPop);

    this.applyColumns();
    this.syncPriceFills();
    this.syncAside();
  }

  /* «Open by default» is server-rendered on the sidebar itself; the toolbar button only learns
     about it here, so the glyph and aria-expanded agree with what is on screen. */
  syncAside() {
    const aside = this.querySelector('[data-facets-aside]');
    const open = Boolean(aside?.classList.contains('is-open'));
    this.page?.classList.toggle('cpage--aside-open', open);
    this.querySelectorAll('.ctoolbar__filter--d[data-facets-toggle]:not([data-facets-dialog])').forEach(
      (button) => button.setAttribute('aria-expanded', String(open))
    );
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPop);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onOutside);
    this.abort?.abort();
    clearTimeout(this.priceTimer);
  }

  /* ---------- Column switcher ---------- */

  applyColumns() {
    for (const [band, key] of COLS) {
      const value = store.get(key);
      const inputs = this.querySelectorAll(`[data-cols-input="${band}"]`);
      if (!inputs.length) continue;
      const match = value && [...inputs].find((input) => input.value === value);
      if (match) match.checked = true;
      else this.checkRenderedColumns(band, inputs);
    }
  }

  /* No stored choice: mark the count the grid is actually rendering, so the control tells the
     truth about the page instead of guessing the merchant's setting. */
  checkRenderedColumns(band, inputs) {
    const track = this.querySelector('.cgrid .list__track');
    if (!track) return;
    const columns = getComputedStyle(track).gridTemplateColumns.split(' ').filter(Boolean).length;
    const match = [...inputs].find((input) => Number(input.value) === columns);
    if (match) match.checked = true;
  }

  setColumns(band, value) {
    const entry = COLS.find(([name]) => name === band);
    if (!entry) return;
    document.documentElement.style.setProperty(entry[2], value);
    store.set(entry[1], value);
  }

  /* ---------- Events ---------- */

  onChange(event) {
    const target = event.target;

    if (target.matches('[data-cols-input]')) {
      this.setColumns(target.dataset.colsInput, target.value);
      return;
    }

    if (target.matches('[data-price-input], [data-price-slider]')) {
      this.normalisePrice(target);
      this.schedulePrice(target.closest('form'));
      return;
    }

    const form = target.closest('[data-facets-form], [data-sort-form]');
    if (!form) return;
    // Sorting from the toolbar popover: close it, the choice is made.
    target.closest('[data-sort-panel]')?.removeAttribute('open');
    this.navigate(urlFromForm(form), { focus: target.id });
  }

  onInput(event) {
    const target = event.target;
    if (!target.matches('[data-price-input], [data-price-slider]')) return;
    this.normalisePrice(target);
    if (target.matches('[data-price-slider]')) this.schedulePrice(target.closest('form'));
  }

  onSubmit(event) {
    const form = event.target.closest('[data-facets-form], [data-sort-form]');
    if (!form) return;
    event.preventDefault();
    this.navigate(urlFromForm(form));
  }

  onClick(event) {
    const more = event.target.closest('[data-facet-more]');
    if (more) {
      const list = more.parentElement?.querySelector('[data-facet-list]');
      if (!list) return;
      const open = list.classList.toggle('is-expanded');
      more.setAttribute('aria-expanded', String(open));
      const label = more.querySelector('.facet__more-label');
      if (label) label.textContent = open ? more.dataset.labelLess : more.dataset.labelMore;
      return;
    }

    const toggle = event.target.closest('[data-facets-toggle]');
    if (toggle) {
      const dialogId = toggle.dataset.facetsDialog;
      const mobile = window.matchMedia('(max-width: 767px)').matches;
      if (dialogId && (mobile || toggle.classList.contains('ctoolbar__filter--m') || !this.querySelector('[data-facets-aside]'))) {
        window.Munkwell?.overlay?.open(dialogId, toggle);
        return;
      }
      const aside = this.querySelector('[data-facets-aside]');
      const open = aside?.classList.toggle('is-open') ?? false;
      this.page?.classList.toggle('cpage--aside-open', open);
      toggle.setAttribute('aria-expanded', String(open));
      return;
    }

    const loadMore = event.target.closest('[data-facets-more]');
    if (loadMore) {
      event.preventDefault();
      this.loadMore(loadMore);
      return;
    }

    const link = event.target.closest('[data-facets-link], [data-facets-clear]');
    if (link && link.href) {
      event.preventDefault();
      this.navigate(link.getAttribute('href'), { top: link.matches('[data-facets-link]') });
    }
  }

  onPop() {
    this.render(window.location.href, { push: false });
  }

  /* The sort popover is a <details>, so it has no dismiss behaviour of its own: Escape closes it
     and returns focus to the summary, a click outside closes it. */
  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const open = this.querySelector('[data-sort-panel][open]');
    if (!open) return;
    open.removeAttribute('open');
    open.querySelector('summary')?.focus();
  }

  onOutside(event) {
    this.querySelectorAll('[data-sort-panel][open]').forEach((panel) => {
      if (!panel.contains(event.target)) panel.removeAttribute('open');
    });
  }

  /* ---------- Price ---------- */

  normalisePrice(target) {
    const wrap = target.closest('[data-facet-price]');
    if (!wrap) return;
    const max = Number(wrap.dataset.max) || 0;
    const fromSlider = wrap.querySelector('[data-price-slider="from"]');
    const toSlider = wrap.querySelector('[data-price-slider="to"]');
    const fromInput = wrap.querySelector('[data-price-input="from"]');
    const toInput = wrap.querySelector('[data-price-input="to"]');
    if (!fromSlider || !toSlider || !fromInput || !toInput) return;

    const which = target.dataset.priceSlider || target.dataset.priceInput;
    const isSlider = Boolean(target.dataset.priceSlider);
    let from = Number(isSlider ? fromSlider.value : fromInput.value || 0);
    let to = Number(isSlider ? toSlider.value : toInput.value || max);
    if (Number.isNaN(from)) from = 0;
    if (Number.isNaN(to)) to = max;
    from = Math.min(Math.max(from, 0), max);
    to = Math.min(Math.max(to, 0), max);
    if (which === 'from' && from > to) to = from;
    if (which === 'to' && to < from) from = to;

    fromSlider.value = String(from);
    toSlider.value = String(to);
    fromInput.value = from > 0 ? String(from) : '';
    toInput.value = to < max ? String(to) : '';

    wrap.style.setProperty('--price-from', max ? `${(from / max) * 100}%` : '0%');
    wrap.style.setProperty('--price-to', max ? `${(to / max) * 100}%` : '100%');
  }

  syncPriceFills() {
    this.querySelectorAll('[data-facet-price] [data-price-slider="from"]').forEach((slider) => {
      this.normalisePrice(slider);
    });
  }

  schedulePrice(form) {
    if (!form) return;
    clearTimeout(this.priceTimer);
    this.priceTimer = window.setTimeout(() => this.navigate(urlFromForm(form)), 500);
  }

  /* ---------- Rendering ---------- */

  navigate(url, options = {}) {
    this.render(url, { push: true, ...options });
  }

  async render(url, { push = true, focus = '', top = false } = {}) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const target = new URL(url, window.location.origin);
    const fetchUrl = new URL(target.href);
    fetchUrl.searchParams.set('section_id', sectionId);

    this.abort?.abort();
    this.abort = new AbortController();
    this.page?.setAttribute('aria-busy', 'true');

    const activeId = focus || document.activeElement?.id || '';
    const asideScroll = this.querySelector('.facets-aside__inner')?.scrollTop ?? 0;

    try {
      const response = await fetch(fetchUrl.href, { signal: this.abort.signal });
      if (!response.ok) throw new Error(String(response.status));
      const markup = await response.text();
      const next = new DOMParser().parseFromString(markup, 'text/html');

      this.swapResults(next);
      this.swapForms(next);
      this.swapCount(next);
      this.updateActiveState(next);

      if (push) window.history.pushState({ mwCollection: true }, '', target.href);
      const aside = this.querySelector('.facets-aside__inner');
      if (aside) aside.scrollTop = asideScroll;
      if (activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
      if (top) this.scrollToResults();
      this.dispatchEvent(new CustomEvent('mw:collection:updated', { bubbles: true, detail: { url: target.href } }));
    } catch (error) {
      if (error.name === 'AbortError') return;
      // The links are real: a failed fetch falls back to a normal page load.
      window.location.href = target.href;
    } finally {
      this.page?.removeAttribute('aria-busy');
      this.syncPriceFills();
      this.applyColumns();
    }
  }

  swapResults(next) {
    const fresh = next.querySelector('[data-facets-results]');
    if (fresh && this.results) this.results.innerHTML = fresh.innerHTML;
  }

  swapCount(next) {
    const fresh = next.querySelector('[data-facets-count]');
    const current = this.querySelector('[data-facets-count]');
    if (fresh && current) current.textContent = fresh.textContent;
  }

  /* Each facet form is replaced in place, so an open dialog keeps its element — and with it its
     open state, its scroll and the overlay's focus trap. Panels the shopper opened stay open. */
  swapForms(next) {
    this.querySelectorAll('[data-facets-form]').forEach((form) => {
      const ctx = form.dataset.facetsCtx;
      const fresh = next.querySelector(`[data-facets-form][data-facets-ctx="${ctx}"]`);
      if (!fresh) return;

      const openKeys = new Set(
        [...form.querySelectorAll('[data-facet-key]')]
          .filter((facet) => facet.querySelector('details')?.open)
          .map((facet) => facet.dataset.facetKey)
      );
      const expanded = new Set(
        [...form.querySelectorAll('[data-facet-key]')]
          .filter((facet) => facet.querySelector('.is-expanded'))
          .map((facet) => facet.dataset.facetKey)
      );

      form.innerHTML = fresh.innerHTML;

      form.querySelectorAll('[data-facet-key]').forEach((facet) => {
        const details = facet.querySelector('details');
        if (details && openKeys.has(facet.dataset.facetKey)) details.open = true;
        if (expanded.has(facet.dataset.facetKey)) {
          const list = facet.querySelector('[data-facet-list]');
          const button = facet.querySelector('[data-facet-more]');
          list?.classList.add('is-expanded');
          if (button) {
            button.setAttribute('aria-expanded', 'true');
            const label = button.querySelector('.facet__more-label');
            if (label) label.textContent = button.dataset.labelLess;
          }
        }
      });
    });
  }

  /* The toolbar itself is not replaced (the sort popover and the sidebar toggle live there), so
     the two things that depend on the result — the active-filter badge and the Clear button —
     are refreshed from the swapped forms. */
  updateActiveState(next) {
    // The apply button names the new result («Vis 3 varer»), so copy the freshly rendered label
    // rather than trying to rebuild a translated, pluralised string in JS.
    const freshApply = next?.querySelector('[data-facets-apply] .button__label');
    const apply = this.querySelector('[data-facets-apply] .button__label');
    if (freshApply && apply) apply.textContent = freshApply.textContent;

    const form = this.querySelector('[data-facets-form]');
    if (!form) return;
    let active = form.querySelectorAll('.facet__input[type="checkbox"]:checked').length;
    form.querySelectorAll('[data-facet-price]').forEach((price) => {
      const from = price.querySelector('[data-price-input="from"]')?.value;
      const to = price.querySelector('[data-price-input="to"]')?.value;
      if (from || to) active += 1;
    });

    this.querySelectorAll('.ctoolbar__badge').forEach((badge) => {
      badge.textContent = String(active);
      badge.hidden = active === 0;
    });
    // The Clear button stays in place and goes quiet, so the footer never changes height.
    this.querySelectorAll('.facets-dialog [data-facets-clear]').forEach((clear) => {
      clear.classList.toggle('is-disabled', active === 0);
    });
  }

  scrollToResults() {
    const anchor = this.querySelector('.ctoolbar') || this.results;
    if (!anchor) return;
    const offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-offset')) || 0;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: anchor.getBoundingClientRect().top + window.scrollY - offset - 16,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }

  /* «Load more» keeps what is on screen and appends the next page, so the shopper never loses
     their place. The URL follows, so a reload lands on the page they were reading. */
  async loadMore(button) {
    const sectionId = this.dataset.sectionId;
    const href = button.getAttribute('href');
    if (!sectionId || !href) return;

    const target = new URL(href, window.location.origin);
    const fetchUrl = new URL(target.href);
    fetchUrl.searchParams.set('section_id', sectionId);
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(fetchUrl.href);
      if (!response.ok) throw new Error(String(response.status));
      const next = new DOMParser().parseFromString(await response.text(), 'text/html');
      const track = this.querySelector('.cgrid .list__track');
      const freshTrack = next.querySelector('.cgrid .list__track');
      if (!track || !freshTrack) throw new Error('no track');

      const first = freshTrack.firstElementChild;
      // Promo cards belong to the list, not to a page: they are already on screen.
      [...freshTrack.children].forEach((item) => {
        if (!item.classList.contains('list__item--promo')) track.append(item);
      });

      const freshPag = next.querySelector('.cpag--more');
      const currentPag = this.querySelector('.cpag--more');
      if (currentPag) {
        if (freshPag) currentPag.innerHTML = freshPag.innerHTML;
        else currentPag.remove();
      }

      window.history.pushState({ mwCollection: true }, '', target.href);
      first?.querySelector('a')?.focus({ preventScroll: true });
    } catch {
      window.location.href = target.href;
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('mw-collection')) customElements.define('mw-collection', Collection);

/* <mw-clamp> — the collection banner's «Height limit». The clamp is CSS (-webkit-line-clamp on
   .cbanner__rte); this only decides whether a toggle is worth showing and flips the class. The
   button starts hidden, so a description that fits never grows one. */
class Clamp extends HTMLElement {
  connectedCallback() {
    this.text = this.querySelector('[data-clamp-text]');
    this.toggle = this.querySelector('[data-clamp-toggle]');
    if (!this.text || !this.toggle) return;

    this.onClick = this.onClick.bind(this);
    this.measure = this.measure.bind(this);
    this.toggle.addEventListener('click', this.onClick);
    window.addEventListener('resize', this.measure);
    this.measure();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.measure);
  }

  measure() {
    if (this.classList.contains('is-open')) return;
    const overflows = this.text.scrollHeight - this.text.clientHeight > 1;
    this.toggle.hidden = !overflows;
  }

  onClick() {
    const open = this.classList.toggle('is-open');
    this.toggle.setAttribute('aria-expanded', String(open));
    this.toggle.textContent = open ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;
  }
}

if (!customElements.get('mw-clamp')) customElements.define('mw-clamp', Clamp);
